import { useCallback, useEffect, useRef, useState } from "react";
import bankService from "../services/bank.service";
import useAuth from "./useAuth";
import { uploadFileToPresignedUrl } from "../utils/s3DirectUpload";
import { listenUploadProgress } from "../utils/uploadSocket";

const POLL_INTERVAL = 2000;

function mergeBatch(current, incoming) {
  if (!incoming) return current;
  if (!current) return incoming;
  return { ...current, ...incoming };
}

export function useBankExcelUpload({ onComplete } = {}) {
  const { auth } = useAuth();
  const [uploadStage, setUploadStage] = useState("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [activeBatch, setActiveBatch] = useState(null);
  const pollRef = useRef(null);
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const finishBatch = useCallback(
    (batch) => {
      stopPolling();
      setUploadStage("idle");
      if (batch) setActiveBatch((current) => mergeBatch(current, batch));
      completeRef.current?.(batch);
    },
    [stopPolling]
  );

  const startPolling = useCallback(
    (batchId) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const res = await bankService.getUploadBatch(batchId);
          const batch = res?.data?.data?.batch;
          if (!batch) return;
          setActiveBatch((current) => mergeBatch(current, batch));
          if (batch.status === "completed" || batch.status === "failed") {
            finishBatch(batch);
          }
        } catch {
          /* keep polling */
        }
      }, POLL_INTERVAL);
    },
    [finishBatch, stopPolling]
  );

  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => {
    const batchId = activeBatch?._id;
    const token = auth?.token;
    const live =
      activeBatch?.status === "processing" || activeBatch?.status === "pending";
    if (!token || !batchId || !live) return undefined;

    return listenUploadProgress(token, batchId, {
      onProgress: (payload) => {
        setActiveBatch((current) =>
          mergeBatch(current, {
            processedRows: payload.processedRows,
            totalRows: payload.totalRows,
            status: payload.status || "processing",
            importNote: payload.message,
            percent: payload.percent,
            successRows: payload.successRows,
            failedRows: payload.failedRows,
            duplicateRows: payload.duplicateRows,
          })
        );
      },
      onComplete: (payload) => {
        finishBatch(payload.batch || payload);
      },
      onFailed: (payload) => {
        setUploadError(payload.errorMessage || "Processing failed");
        finishBatch({
          _id: batchId,
          status: "failed",
          errorMessage: payload.errorMessage || "Processing failed",
        });
      },
    });
  }, [activeBatch?._id, activeBatch?.status, auth?.token, finishBatch]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploadError("");
    setActiveBatch(null);

    try {
      setUploadStage("presigning");
      const presignRes = await bankService.presignUpload({
        fileName: file.name,
        contentType:
          file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const { batchId, uploadUrl, contentType, storage } = presignRes?.data?.data || {};
      if (!batchId || !uploadUrl) {
        throw new Error("Server did not return upload URL. Check storage config.");
      }

      setUploadStage("uploading");
      setUploadProgress(0);
      const mime =
        contentType ||
        file.type ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const proxyThroughApi = async () => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("batchId", batchId);
        await bankService.proxyUpload(formData);
      };
      const useServerDisk =
        storage === "local" || String(uploadUrl).startsWith("local-proxy");
      if (useServerDisk) {
        await proxyThroughApi();
        setUploadProgress(100);
      } else {
        try {
          await uploadFileToPresignedUrl(file, uploadUrl, (loaded, total) => {
            setUploadProgress(Math.round((loaded / total) * 100));
          }, mime);
        } catch {
          await proxyThroughApi();
        }
      }

      setUploadStage("processing");
      await bankService.completeUpload({ batchId });

      setActiveBatch({
        _id: batchId,
        status: "processing",
        processedRows: 0,
        totalRows: 0,
        successRows: 0,
        failedRows: 0,
        duplicateRows: 0,
      });
      startPolling(batchId);
    } catch (err) {
      setUploadStage("idle");
      setUploadError(err?.response?.data?.message || err?.message || "Upload failed");
    }
  };

  const dismissBatch = () => setActiveBatch(null);

  const uploadButtonLabel = () => {
    if (uploadStage === "presigning") return "Preparing…";
    if (uploadStage === "uploading") return `Uploading ${uploadProgress}%`;
    if (uploadStage === "processing") return "Processing…";
    return "Upload Excel";
  };

  return {
    uploadStage,
    uploadProgress,
    uploadError,
    setUploadError,
    activeBatch,
    dismissBatch,
    uploadFile,
    uploadButtonLabel,
    isBusy: uploadStage !== "idle",
  };
}
